/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Single source of truth for product capabilities. Use for homepage features
 * grid, /features page, and marketing. Only list what the product actually provides.
 */

import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Calculator,
  FileText,
  Layers,
  Receipt,
  Shield,
  Sparkles,
  Wallet,
} from "lucide-react";

export type ProductFeatureCard = {
  icon: LucideIcon;
  title: string;
  description: string;
  includes: string[];
};

/**
 * Feature cards for the homepage Features section. Each includes 4–6
 * concrete capabilities. No takeoffs-from-plans claims.
 */
export const productFeatureCards: ProductFeatureCard[] = [
  {
    icon: Calculator,
    title: "Estimates & proposals",
    description:
      "Build estimates with material, labor, and equipment line items. Turn them into proposals and export to PDF.",
    includes: [
      "Line items with material, labor, and equipment—with markup and totals",
      "Estimate and proposal templates you can customize",
      "Estimate pipeline and status (draft, sent, won, lost)",
      "Generate PDF proposals from estimates",
      "Revision and version tracking",
    ],
  },
  {
    icon: Sparkles,
    title: "Documents & AI",
    description:
      "Store documents, organize by project, and use AI-assisted cost matching in takeoffs.",
    includes: [
      "Upload and organize documents; folder or category-style structure",
      "AI document analysis to extract and structure data for estimation (coming soon)",
      "AI-assisted cost matching: suggest materials, labor, or equipment for takeoff lines",
      "In-app AI assistant (Hank) for estimation and project questions",
      "Database analysis to explore your data with natural language (coming soon)",
    ],
  },
  {
    icon: FileText,
    title: "Takeoffs & data import",
    description:
      "Bring takeoff and project data into one place. Import from Excel and other documents; use AI to help structure it for estimates.",
    includes: [
      "Upload Excel, CSV, and PDF documents",
      "AI helps organize and structure your data for estimating",
      "Work areas and line items with quantities and units",
      "Batch match takeoff lines to your materials, labor, and equipment",
      "Create estimates directly from takeoff data",
    ],
  },
  {
    icon: Layers,
    title: "Projects",
    description:
      "Create projects, attach estimates and budgets, track tasks and actual costs.",
    includes: [
      "Create projects and link estimates and proposals",
      "Tasks and timelines",
      "Budget vs. actual cost tracking",
      "Project status and progress",
      "Dashboard with active and at-risk projects",
    ],
  },
  {
    icon: Wallet,
    title: "Costs: materials, labor & equipment",
    description:
      "Central databases for materials, labor tasks, and equipment. Use them in estimates and cost tracking.",
    includes: [
      "Materials database with units, costs, and categories",
      "Labor tasks and rates by trade or role",
      "Equipment with hourly or daily rates",
      "Cost profiles (markup, overhead, tax) at org or project level",
      "Geographic cost factors for regional pricing",
    ],
  },
  {
    icon: Building2,
    title: "Clients, contacts & suppliers",
    description:
      "Keep clients, contacts, and suppliers in one place and attach them to projects and estimates.",
    includes: [
      "Client and contact records with address and notes",
      "Client pipeline and stage (lead, prospect, etc.)",
      "Suppliers with name, contact, and rating",
      "Link clients to projects, estimates, and proposals",
      "Job sites per client",
    ],
  },
  {
    icon: Receipt,
    title: "Budgets & invoices",
    description:
      "Budgets with line items and optional QuickBooks import. Invoices tied to estimates and projects.",
    includes: [
      "Budgets with line items; track against actuals",
      "QuickBooks integration: import budgets and sync chart of accounts",
      "Budget reports and variance views",
      "Invoices from estimates or projects",
      "Invoice status and history",
    ],
  },
  {
    icon: Shield,
    title: "Organization, security & collaboration",
    description:
      "Multi-tenant, roles, and org-level settings. Cloud-based so your team can work from one source of truth.",
    includes: [
      "Multi-tenant: each organization’s data isolated by design",
      "Timezone, currency, and date format per organization",
      "Branding: logo, colors, company tagline",
      "Team collaboration: invite users, assign roles",
      "Audit trails and access control; cloud-based, 24/7",
    ],
  },
];

/**
 * Additional product areas for the /features page "Full list" section.
 * These are either subsumed in the cards above or are smaller surfaces.
 */
export const productFeatureAreasDetail: {
  name: string;
  includes: string[];
}[] = [
  {
    name: "Calendar",
    includes: [
      "Calendar events",
      "Schedule views",
      "Link events to projects or clients",
    ],
  },
  {
    name: "Analytics",
    includes: [
      "Dashboard with project, estimate, and cost metrics",
      "Business intelligence and custom analytics views",
      "Reporting and export",
    ],
  },
  {
    name: "Templates & resources",
    includes: [
      "Estimate and proposal templates",
      "Organization-level templates and reusable resources",
      "Template usage and metrics",
    ],
  },
  {
    name: "Services",
    includes: [
      "Service records and scheduling",
      "Link services to clients",
      "Service-specific views and workflows",
    ],
  },
  {
    name: "Notifications",
    includes: [
      "In-app notifications",
      "Notification preferences and rules",
      "Template-based notification content",
    ],
  },
  {
    name: "Integrations",
    includes: [
      "QuickBooks",
      "API access (Professional and above)",
      "Webhook and integration patterns",
    ],
  },
];
